import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import styled, { keyframes } from 'styled-components';
import { animate } from 'animejs';

// About section styling with animated background
const AboutSection = styled.section`
  padding: 2rem 2rem 1rem 2rem;
  min-height: 80vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background-color: ${({ theme }) => theme.background};
  position: relative;
  overflow: hidden;
`;

// Underline animation for header
const Header = styled.h2`
  font-size: 2rem;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.accent};
  position: relative;
  display: inline-block;

  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 4px;
    background-color: ${({ theme }) => theme.accent};
    bottom: -10px;
    left: 0;
    animation: underline 1s infinite alternate;
  }

  @keyframes underline {
    from { width: 0; }
    to   { width: 100%; }
  }
`;

// Container for text content
const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
`;

// Paragraph styling
const Text = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.color};
  line-height: 1.6;
  margin-top: 1rem;
`;

// Highlighted inline text
const Highlight = styled.span`
  font-weight: bold;
  color: ${({ theme }) => theme.accent};
  position: relative;
  display: inline-block;
`;

// Floating circle animation
const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-20px); }
`;

// Decorative circle
const AnimatedCircle = styled(motion.div)`
  position: absolute;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: ${({ theme }) => theme.accent};
  opacity: 0.1;
  animation: ${float} 6s ease-in-out infinite;
`;

// Fade-in container variants
const containerVariants = {
  hidden:  { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeInOut' } },
};

const About = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    // Animate only <Highlight> spans in an infinite loop, slowed by 50%
    animate(
      `#${sectionRef.current.id} .highlight`,
      {
        y: [
          { value: '-2.75rem', duration: 1400, easing: 'outExpo' },
          { value: '0',          duration: 1800, easing: 'outBounce', delay: 300 }
        ],
        // rotate: { from: '-1turn', to: '0turn', delay: 0 },
        delay: (el, i) => i * 100,
        easing: 'inOutCirc',
        loop: true
      }
    );
  }, []);

  return (
    <AboutSection id="about" ref={sectionRef}>
      {/* Floating background circles */}
      <AnimatedCircle style={{ top: '20%', left: '10%' }} />
      <AnimatedCircle style={{ bottom: '30%', right: '15%' }} />
      <AnimatedCircle style={{ top: '50%', left: '70%' }} />

      <motion.div variants={containerVariants} initial="hidden" animate="visible">
        <Header>About Me</Header>
        <Content>
          <Text>
            Hello! I'm <Highlight className="highlight">Harsh Dhama</Highlight>, a dedicated <Highlight className="highlight">Full Stack Web Developer</Highlight> and <Highlight className="highlight">Cybersecurity Enthusiast</Highlight> with a deep passion for creating secured, high-performance web applications using React, Node.js, and Express.
          </Text>
          <Text>
            My journey in tech is driven by an insatiable <Highlight className="highlight">learning mindset</Highlight>. I continuously explore emerging tools—from container orchestration with Docker to vulnerability assessments—ensuring my solutions stay cutting-edge and resilient.
          </Text>
          <Text>
            <Highlight className="highlight">Leadership</Highlight> and <Highlight className="highlight">mentorship</Highlight> are at the core of my practice: I lead code reviews, host knowledge-sharing sessions, and guide junior developers through best practices, fostering <Highlight className="highlight">collaborative environments</Highlight> where everyone grows together.
          </Text>
          <Text>
            In previous roles, I spearheaded the NeuroPeace project, championing agile workflows and delivering features on schedule. Whether architecting APIs or conducting security audits, I thrive on balancing technical excellence with <Highlight className="highlight">team empowerment</Highlight>.
          </Text>
          <Text>
            Outside of coding, I’m actively contributing to open-source, speaking at local meetups, and strategizing my next entrepreneurial venture combining tech innovation with <Highlight className="highlight">real-world impact</Highlight>.
          </Text>
        </Content>
      </motion.div>
    </AboutSection>
  );
};

export default About;
